import { AfterViewInit, Component, ElementRef, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { LegacyUibModalService } from '../modals/legacy-uib-modal.service';
import { UIRouter, TransitionService } from '@uirouter/core';
import { TranslateService } from '@ngx-translate/core';
import moment                from 'moment';

import { ApiService }      from '../api/api.service';
import { CompanyService }  from '../companies/company.service';
import { CustomerModel }   from '../customers/models/customer';
import { EventBusService } from '../core/services/event-bus.service';
import { LoanCarScheduleFactory } from './loan-car-schedule.schedule.service';
import { MessagesService } from '../messages/messages.service';
import { SelectOption }    from '../core/models/select-option';

import CreateCarLoanHtml from './loan-car-schedule.create.html';

@Component({
  selector   : 'wor-loan-car-schedule',
  template: require('./loan-car-schedule.component.html')
})
export class LoanCarScheduleComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('calendarEl', { static: false }) calendarEl: ElementRef;

  @Input() customer: CustomerModel;
  @Input() groups: any;
  @Input() settings: any;
  @Input() states: SelectOption[];

  calendar: any;
  private calendarInstance: any;
  loanCarSchedule: any;
  loanVehicleId = '';
  simpleView = false;

  private deregisterExit: any;
  private deregisterRetain: any;

  constructor (
    private $uibModal: LegacyUibModalService,
    private loanCarScheduleFactory: LoanCarScheduleFactory,
    private apiService      : ApiService,
    public  companyService  : CompanyService,
    private eventBus: EventBusService,
    private messagesService : MessagesService,
    private router          : UIRouter,
    private transitionService: TransitionService,
    private translateService: TranslateService
  ) {}

  ngOnInit () {
    const stateParams = this.router.globals.params;
    this.loanCarSchedule = this.loanCarScheduleFactory.create(this.settings, this.groups);
    // TODO: settings/groups were previously placed on $rootScope for legacy AJS templates.
    // They are now only available via component properties.

    const self = this;

    this.calendar = {
      allDayDefault: false, allDaySlot: false,
      columnFormat: this.companyService.company?.language === 'en-us' ? 'ddd M/D' : 'ddd D/M',
      contentHeight: 'auto',
      dayClick: date => this.loanCarSchedule.dayClick(date),
      defaultDate: stateParams.date || moment().format('YYYY-MM-DD'),
      defaultView: stateParams.view || 'multiColAgendaDay',
      droppable: false, loanBackgroundColor: 'transparent',
      eventDrop: loan => {
        this.loanCarSchedule.updateScheduledLoan(loan)
        .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.LOAN'), 'success'))
        .catch(err => this.messagesService.error(err))
        .finally(() => this.loanCarSchedule.view?.name === 'multiColAgendaDay' ? this.router.stateService.reload() : null);
      },
      eventRender: (loan, element) => {
        // TODO: $compile usage retained — requires AJS scope for legacy loan templates.
        // Remove when loan car event rendering is converted to Angular.
        const injector = (window as any).angular?.element(document.body).injector();
        const $rootScope = injector?.get('$rootScope');
        const $compile = injector?.get('$compile');
        if ($rootScope && $compile) {

          element.html($compile(this.loanCarSchedule.getLoanTemplate(loan))($rootScope.$new()));

        }
      },
      events: (start, end, timezone, callback) => this.loanCarSchedule.configureLoans(start, end, timezone, callback),
      loanTextColor: 'transparent', firstDay: 1,
      customButtons: {
        todayCustom: {
          text: this.translateService.instant('GENERAL_SPACE.BOOKING_DAYS.TODAY'),
          click: () => this.loanCarSchedule.buildRouteAndReload({
            date: moment().format('YYYY-MM-DD'), includeDate: true,
            loanVehicle: stateParams.loanVehicle, loanVehicleId: stateParams.loanVehicleId,
            viewSingleLoanVehicle: stateParams.viewSingleLoanVehicle
          })
        }
      },
      header: { left: 'title', center: '', right: 'multiColAgendaDay,todayCustom,prev,next' },
      lazyFetching: true,
      maxTime: this.loanCarSchedule.shopCloses || '18:00:00',
      minTime: this.loanCarSchedule.shopOpens || '08:00:00',
      slotDuration: '00:15:00', timeFormat: '', timezone: 'UTC',
      viewRender: view => this.loanCarSchedule.viewRender(view),
      views: {
        multiColAgendaDay: {
          columnHeaders: this.loanCarSchedule.loanVehicleGroup?.headerColumns || this.loanCarSchedule.loanVehicleGroup?.plate_numbers,
          duration: { days: 1 }, editable: true,
          formatDate: this.companyService.company?.language === 'en-us' ? 'mm/dd' : 'dd/mm',
          numColumns: this.loanCarSchedule.loanVehicleGroup?.plate_numbers?.length,
          type: 'multiColAgenda'
        }
      }
    };

    this.deregisterExit = this.transitionService.onExit({ exiting: 'app.loan-car' }, transition => {
      if (transition.to().name !== 'app.loan-car') this.loanCarSchedule.clearStyles();
    });

    this.deregisterRetain = this.transitionService.onRetain({ retained: 'app.loan-car' }, () => {
      if (stateParams.view === 'multiColAgendaDay' && stateParams.loanVehicleId) this.router.stateService.reload();
    });

    this.eventBus.broadcast('pg-tooltip-hide');
  }

  ngAfterViewInit () {
    if (this.calendarEl) {

      this.calendarInstance = $(this.calendarEl.nativeElement);

      this.calendarInstance.fullCalendar(this.calendar);

    }
  }

  ngOnDestroy () {
    if (this.calendarInstance?.fullCalendar) {

      this.calendarInstance.fullCalendar('destroy');

    }
    if (this.deregisterExit) this.deregisterExit();
    if (this.deregisterRetain) this.deregisterRetain();
  }

  createLoan () : Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      templateUrl: CreateCarLoanHtml, windowClass: 'x-lg-modal', controller: 'CreateLoanCarCtrl',
      resolve: {
        status: () => this.apiService.get('/system/loan_status_types/', null, 'loanStatusTypes'),
        customer: () => this.customer,
        loan_vehicle: () => this.loanVehicleId ? this.apiService.get('/loan_vehicle', this.loanVehicleId) : [],
        states: () => this.states || []
      }
    } as any).result;
  }

  goToLoan ( id : string, type : string ) : void {


    this.eventBus.broadcast('pg-tooltip-hide');


    this.loanCarSchedule.goToLoan(id, type);


  }

  view ( loan : any ) : void {


    this.loanCarSchedule.viewLoan(loan);


    this.eventBus.broadcast('pg-tooltip-hide');


  }

  viewDate ( index : number ) : void {


    this.loanCarSchedule.viewDate(this.loanCarSchedule.loans[index]?.date);


    this.eventBus.broadcast('pg-tooltip-hide');


  }
}
